import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { getDatabase, ref, onValue } from "firebase/database";
import FunctionsHeader from "../components/FunctionsHeader";
import Breadcrumb from "../components/Breadcrumb";

const IngredienteRoot = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: var(--color-gray-200);
  font-family: var(--font-roboto);
`;

const MainContent = styled.div`
  flex: 1;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const SearchBar = styled.div`
  width: 100%;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  background-color: var(--color-white);
  border-radius: var(--br-5xs);
  overflow: hidden;
  height: 36px;
`;

const StyledAutocomplete = styled.div`
  flex: 1;
  .MuiInputBase-root {
    padding: 0;
    height: 36px;
    border: none;
    outline: none;
    font-size: 1rem;
  }

  .MuiOutlinedInput-notchedOutline {
    border: none;
  }

  .MuiAutocomplete-inputRoot {
    padding: 8px 10px;
  }

  .MuiAutocomplete-option {
    background-color: var(--color-white) !important;
    color: var(--color-black) !important;
  }
`;

const SearchButton = styled.button`
  width: 36px;
  height: 36px;
  background-color: var(--color-darkorange);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const ClientsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
  width: 100%;
`;

const ClientCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
`;

const ClientImage = styled.img`
  width: 100%;
  max-width: 220px;
  max-height: 156px;
  border-radius: var(--br-5xs);
  object-fit: cover;
  border: 1px solid orange;
`;

const ClientName = styled.div`
  margin-top: 10px;
  font-size: 1rem;
  color: var(--color-white);
`;

const Ingrediente = () => {
  const [ingredients, setIngredients] = useState([]);
  const [filteredIngredients, setFilteredIngredients] = useState([]);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const db = getDatabase();
    const ingredientsRef = ref(db, 'ingredients'); // Adjust this to match your Firebase structure

    onValue(ingredientsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const formattedIngredients = Object.keys(data).map(key => ({
          id: key,
          name: data[key].name,
          image: data[key].image || null, // Assume `image` holds the base64 data if available
        }));
        setIngredients(formattedIngredients);
        setFilteredIngredients(formattedIngredients);
      }
    });
  }, []);

  useEffect(() => {
    if (query.length > 2) {
      const filtered = ingredients.filter(ingredient =>
        ingredient.name && ingredient.name.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredIngredients(filtered);
    } else {
      setFilteredIngredients(ingredients);
    }
  }, [query, ingredients]);

  const onIngredientClick = useCallback((ingredientPath) => {
    navigate(ingredientPath);
  }, [navigate]);

  return (
    <IngredienteRoot>
      <FunctionsHeader
        onLogoClick={() => navigate("/home")}
        onClientClick={() => navigate("/clientes")}
        onIngredientClick={() => navigate("/ingredientes")}
      />
      <Breadcrumb path="Página inicial > Ingredientes > Selecione um ingrediente" />
      <MainContent>
        <ContentWrapper>
          <SearchBar>
            <StyledAutocomplete>
              <input
                type="text"
                placeholder="Pesquise por ingrediente"
                onChange={(e) => setQuery(e.target.value)}
                style={{ width: "100%", height: "100%", border: "none", padding: "8px 10px" }}
              />
            </StyledAutocomplete>
            <SearchButton>
              <img src="/group-4.svg" alt="Search Icon" />
            </SearchButton>
          </SearchBar>
          <ClientsGrid>
            {filteredIngredients.map(ingredient => (
              <ClientCard key={ingredient.id} onClick={() => onIngredientClick(`/ingrediente/${ingredient.id}`)}>
                <ClientImage
                  src={ingredient.image ? `data:image/png;base64,${ingredient.image}` : "/default-ingredients.jpg"}
                  alt={ingredient.name}
                />
                <ClientName>{ingredient.name}</ClientName>
              </ClientCard>
            ))}
          </ClientsGrid>
        </ContentWrapper>
      </MainContent>
    </IngredienteRoot>
  );
};

export default Ingrediente;
